import { useCallback, useMemo, useRef, useState } from 'react';
import { onValidateField } from './_validators';

/*
    🔥 useForm — ESTADO, VALIDACIÓN Y ENVÍO

    Es lo que faltaba para que los 47 campos dejen de ser piezas sueltas.

    ⚠️ CUÁNDO VALIDA, Y POR QUÉ

    Un campo NO muestra error hasta que el usuario lo tocó (onBlur) o
    hasta que intentó enviar. Validar mientras se escribe la primera
    letra del correo y gritarle "inválido" es hostil.

    Una vez que un campo ya mostró error, SÍ se revalida al escribir:
    ahí el usuario está corrigiendo y necesita ver cuándo lo logró.

    USO:

    const form = useForm({
        initialValues: { email: '', pass: '' },
        schema: {
            email: { required: true, email: true },
            pass:  { required: true, minLength: 8 },
        },
        onSubmit: async (values) => { await api.login(values); },
    });

    <FieldInput {...form.field('email')} label="Correo" />
    <FormButton type="submit" isLoading={form.isSubmitting}>Entrar</FormButton>
*/

export const useForm = ({
    initialValues = {},
    schema = {},
    onSubmit = null,
    validateOnBlur = true,
    validateOnChange = true,   // solo en campos ya tocados
} = {}) => {

    const [useValues, setValues] = useState(initialValues);
    const [useErrors, setErrors] = useState({});
    const [useTouched, setTouched] = useState({});
    const [useIsSubmitting, setIsSubmitting] = useState(false);
    const [useSubmitCount, setSubmitCount] = useState(0);

    const REF__useRefInitial = useRef(initialValues);

    // ---------------------------- 🔥 VALIDACIÓN
    const CB__onValidateOne = useCallback((name, value, allValues) => (
        onValidateField(value, schema[name] ?? {}, allValues)
    ), [schema]);

    const CB__onValidateAll = useCallback((allValues) => {

        const NEXT = {};

        Object.keys(schema).forEach((name) => {
            const ERROR = CB__onValidateOne(name, allValues[name], allValues);
            if (ERROR) NEXT[name] = ERROR;
        });

        return NEXT;
    }, [schema, CB__onValidateOne]);

    // ---------------------------- 🔥 CAMBIOS
    const CB__onSetValue = useCallback((name, value) => {

        setValues((prev) => {

            const NEXT = { ...prev, [name]: value };

            // Revalida solo si el campo ya mostró error alguna vez
            if (validateOnChange && useTouched[name]) {
                const ERROR = CB__onValidateOne(name, value, NEXT);
                setErrors((prevErrors) => ({ ...prevErrors, [name]: ERROR }));
            }

            return NEXT;
        });

    }, [useTouched, validateOnChange, CB__onValidateOne]);

    const CB__onChange = useCallback((event) => {

        const { name, type, value, checked } = event.target;

        CB__onSetValue(name, type === 'checkbox' ? checked : value);

    }, [CB__onSetValue]);

    const CB__onBlur = useCallback((event) => {

        const { name } = event.target;

        setTouched((prev) => ({ ...prev, [name]: true }));

        if (!validateOnBlur) return;

        setValues((current) => {
            const ERROR = CB__onValidateOne(name, current[name], current);
            setErrors((prev) => ({ ...prev, [name]: ERROR }));
            return current;
        });

    }, [validateOnBlur, CB__onValidateOne]);

    // ---------------------------- 🔥 ENVÍO
    const CB__onHandleSubmit = useCallback(async (event) => {

        event?.preventDefault?.();

        setSubmitCount((n) => n + 1);

        const ERRORS = CB__onValidateAll(useValues);

        // Al enviar, TODO se marca como tocado: si no, los errores de
        // los campos que nunca tocó no se verían.
        const ALL_TOUCHED = {};
        Object.keys(schema).forEach((name) => { ALL_TOUCHED[name] = true; });

        setTouched(ALL_TOUCHED);
        setErrors(ERRORS);

        if (Object.keys(ERRORS).length > 0) {

            /*
                Lleva el foco al primer campo con error.

                ⚠️ El id del DOM ya no es el nombre del campo (dos formularios
                abiertos a la vez tendrian ids repetidos), asi que lo
                buscamos por el atributo name, que es el que identifica al
                campo dentro de ESTE formulario.

                Y el scroll es 'nearest': con 'center' se mueven tambien los
                contenedores de arriba y salta toda la pagina.
            */
            const FIRST = Object.keys(ERRORS)[0];

            const NODE = document.querySelector(`[data-field="${FIRST}"]`)
                ?? document.getElementById(FIRST);

            NODE?.focus?.();
            NODE?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });

            return { isValid: false, errors: ERRORS };
        }

        if (!onSubmit) return { isValid: true, values: useValues };

        setIsSubmitting(true);

        try {
            await onSubmit(useValues);
            return { isValid: true, values: useValues };
        } catch (err) {
            // Errores del backend por campo: { email: 'Ya está registrado' }
            if (err?.fields) setErrors(err.fields);
            return { isValid: false, error: err };
        } finally {
            setIsSubmitting(false);
        }

    }, [useValues, schema, CB__onValidateAll, onSubmit]);

    // ---------------------------- 🔥 UTILIDADES
    const CB__onReset = useCallback((next = null) => {
        setValues(next ?? REF__useRefInitial.current);
        setErrors({});
        setTouched({});
        setSubmitCount(0);
    }, []);

    const CB__onSetError = useCallback((name, message) => {
        setErrors((prev) => ({ ...prev, [name]: message }));
        setTouched((prev) => ({ ...prev, [name]: true }));
    }, []);

    const MEMO__IS_DIRTY = useMemo(() => (
        JSON.stringify(useValues) !== JSON.stringify(REF__useRefInitial.current)
    ), [useValues]);

    const MEMO__IS_VALID = useMemo(() => (
        Object.values(useErrors).every((e) => !e)
    ), [useErrors]);

    /*
        🔥 field(name) — el atajo

        Devuelve todo lo que un Field* necesita. El error solo se pasa
        si el campo fue tocado: así no aparece antes de tiempo.
    */
    const CB__field = useCallback((name, options = {}) => ({
        id: name,
        name,
        value: useValues[name] ?? options.emptyValue ?? '',
        onChange: CB__onChange,
        onBlur: CB__onBlur,
        error: useTouched[name] ? (useErrors[name] ?? null) : null,
        isRequired: !!schema[name]?.required,
    }), [useValues, useErrors, useTouched, schema, CB__onChange, CB__onBlur]);

    /*
        🔥 fieldValue(name) — para los campos que NO devuelven un evento

        FieldTags, FieldSelectSearch, FieldFile y compañía llaman a
        onChange con el valor pelado, no con un event.
    */
    const CB__fieldValue = useCallback((name, options = {}) => ({
        id: name,
        value: useValues[name] ?? options.emptyValue ?? null,
        onChange: (value) => CB__onSetValue(name, value),
        error: useTouched[name] ? (useErrors[name] ?? null) : null,
        isRequired: !!schema[name]?.required,
    }), [useValues, useErrors, useTouched, schema, CB__onSetValue]);

    return {
        values: useValues,
        errors: useErrors,
        touched: useTouched,
        isSubmitting: useIsSubmitting,
        isDirty: MEMO__IS_DIRTY,
        isValid: MEMO__IS_VALID,
        submitCount: useSubmitCount,

        field: CB__field,
        fieldValue: CB__fieldValue,

        onChange: CB__onChange,
        onBlur: CB__onBlur,
        onSubmit: CB__onHandleSubmit,
        setValue: CB__onSetValue,
        setValues,
        setError: CB__onSetError,
        reset: CB__onReset,
        validate: () => CB__onValidateAll(useValues),
    };
};
